/**
 * Shipping Threshold Progress Bar Component
 * Shows progress towards free shipping threshold and recommends products
 */

class ShippingProgressBar extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.getAttribute('threshold') || '400') * 100; // in cents
    this.catalogHref = this.getAttribute('catalog') || '/collections/all';
    this.recommendations = [];
  }

  connectedCallback() {
    this.render();
    this.loadRecommendations();
    this.listenForCartUpdates();
  }

  async getCartTotal() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return {
        total: cart.total_price,
        items: cart.items
      };
    } catch (e) {
      console.error('Error fetching cart:', e);
      return { total: 0, items: [] };
    }
  }

  async loadRecommendations() {
    try {
      // Fetch featured collection or use product recommendations
      const response = await fetch('/collections/all/products.json?limit=10');
      const data = await response.json();
      
      this.recommendations = (data.products || [])
        .filter((p) => {
          const title = (p.title || '').toLowerCase();
          if (title.includes('14k') || title.includes('heirloom')) return false;
          return p.variants && p.variants[0];
        })
        .slice(0, 3);
      
      this.updateRecommendations();
    } catch (e) {
      console.error('Error loading recommendations:', e);
    }
  }

  listenForCartUpdates() {
    // Listen for cart updates
    document.addEventListener('cart:updated', () => this.render());
    
    // Poll for changes (fallback)
    setInterval(() => this.render(), 5000);
  }

  async render() {
    const { total, items } = await this.getCartTotal();
    const remaining = this.threshold - total;
    const progress = Math.min(100, (total / this.threshold) * 100);
    const hasReachedThreshold = remaining <= 0;

    this.innerHTML = `
      <div class="shipping-progress ${hasReachedThreshold ? 'shipping-progress--complete' : ''}">
        <div class="shipping-progress__bar-container">
          <div class="shipping-progress__bar" style="width: ${progress}%"></div>
        </div>
        <div class="shipping-progress__text">
          ${hasReachedThreshold 
            ? `<span class="shipping-progress__success">✓ You've unlocked FREE SHIPPING</span>`
            : `<span>Add <strong>${this.formatMoney(remaining)}</strong> more for <strong>FREE SHIPPING</strong></span>`
          }
        </div>
        ${!hasReachedThreshold && this.recommendations.length > 0 ? `
          <div class="shipping-progress__recommendations">
            <span class="shipping-progress__rec-label">Recommended:</span>
            <div class="shipping-progress__rec-items" data-recommendations></div>
          </div>
        ` : ''}
        ${hasReachedThreshold ? `
          <div class="shipping-progress__upsell">
            <span class="shipping-progress__upsell-text">You might also love:</span>
            <a href="${this.catalogHref}" class="shipping-progress__upsell-link">Explore the collection →</a>
          </div>
        ` : ''}
      </div>
    `;

    if (!hasReachedThreshold) {
      this.updateRecommendations();
    }
  }

  updateRecommendations() {
    const container = this.querySelector('[data-recommendations]');
    if (!container || !this.recommendations.length) return;

    container.innerHTML = this.recommendations.map(product => `
      <div class="shipping-progress__rec-item">
        <img 
          src="${product.images[0]?.src || ''}" 
          alt="${product.title}"
          class="shipping-progress__rec-image"
          loading="lazy"
        />
        <div class="shipping-progress__rec-info">
          <span class="shipping-progress__rec-title">${product.title}</span>
          <span class="shipping-progress__rec-price">${this.formatMoney(product.variants[0].price)}</span>
        </div>
        <button 
          class="shipping-progress__rec-add button button--small"
          data-variant-id="${product.variants[0].id}"
        >
          Add
        </button>
      </div>
    `).join('');

    // Add click handlers
    container.querySelectorAll('[data-variant-id]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.variantId;
        btn.disabled = true;
        btn.textContent = 'Adding...';
        
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          
          // Refresh cart display
          document.dispatchEvent(new CustomEvent('cart:updated'));
          this.render();
          
          // Track event
          if (window.analytics) {
            window.analytics.track('Free Shipping Upsell Added', {
              variant_id: variantId
            });
          }
        } catch (e) {
          console.error('Error adding to cart:', e);
          btn.disabled = false;
          btn.textContent = 'Add';
        }
      });
    });
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
}

customElements.define('shipping-progress-bar', ShippingProgressBar);
